import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { SEOHead } from '../components';
import {
  PATiFollowTheClockDiagram,
  PATiSessionOrchestrationDiagram,
  PATiReadinessGateDiagram,
} from '../components/executive';
import {
  ArrowLeft,
  ArrowRight,
  Lock,
  Server,
  ShieldCheck,
  CheckCircle2,
  Building2,
  LineChart,
} from 'lucide-react';

const ownership = [
  {
    icon: Lock,
    title: 'Models In Your Perimeter',
    detail: 'Decision logic runs on infrastructure you control.',
  },
  {
    icon: ShieldCheck,
    title: 'Private Inference',
    detail: 'Model help without shipping your context to an outside API.',
  },
  {
    icon: Server,
    title: 'Self-Hosted Or Managed',
    detail: 'Your infrastructure or ours. The logic stays yours either way.',
  },
];

const principalConcerns = [
  'No overnight or weekend exposure — every session ends flat',
  'Clean daily attribution, because the day starts and ends at zero',
  'A structure you can walk counsel and investors through',
];

const deskConcerns = [
  'The day\u2019s list built before the open, not assembled during it',
  'Stops and portfolio supervision as services, not checkboxes',
  'The close handled without someone manually unwinding the book',
];

const demoItems = [
  'The day\u2019s equities and options list being built before the open',
  'A readiness gate failing on purpose, and capital not moving',
  'The end-of-session flatten, with every position closed out',
  'Where your models sit, and what never leaves your perimeter',
];

const PATi = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const eyebrow = 'text-xs uppercase tracking-[0.35em] text-slate-400';
  const bodyStrong = isDark ? 'text-slate-300' : 'text-slate-700';
  const bodyMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const cardShell = isDark
    ? 'bg-white/[0.03] border border-white/10'
    : 'bg-white border border-slate-200 shadow-sm';

  return (
    <div
      className={
        isDark ? 'bg-[#070b14] text-slate-100' : 'bg-white text-slate-800'
      }
    >
      <SEOHead
        title="PATi — Private Algorithmic Trading Int'l"
        description="A day trading operating system for equities and listed options. Each market gets its own daily list, worked through the session and closed out before the bell. Fail-closed readiness gates, and models that stay inside your perimeter."
        type="website"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(ellipse 80% 55% at 90% 0%, rgba(45,212,191,0.2), transparent 50%), radial-gradient(ellipse 60% 45% at 5% 95%, rgba(37,99,235,0.18), transparent 48%)'
              : 'radial-gradient(ellipse 80% 55% at 90% 0%, rgba(45,212,191,0.12), transparent 50%), radial-gradient(ellipse 60% 45% at 5% 95%, rgba(37,99,235,0.08), transparent 48%)',
          }}
        />
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20">
          <Link
            to="/"
            className={`inline-flex items-center gap-2 text-sm font-medium mb-8 ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-700 hover:text-cyan-900'}`}
          >
            <ArrowLeft size={16} aria-hidden />
            Shared Oxygen
          </Link>

          <div className="grid lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] gap-10 lg:gap-14 items-center">
            <div className="space-y-5 max-w-xl">
              <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">
                PATi
              </h1>
              <p className={`text-sm font-medium ${bodyMuted}`}>
                Private Algorithmic Trading Int&apos;l
              </p>

              <h2 className="text-3xl lg:text-[2.35rem] font-bold leading-[1.14] tracking-tight">
                New York Closes Flat. London Opens. Nobody Wakes Up.
              </h2>

              <p className={`text-lg leading-relaxed ${bodyStrong}`}>
                PATi is a day trading operating system. For each market it builds
                the day&apos;s equities and options list before the open, works
                that list through the session, and closes every open position
                before the close. Then the next market starts its own day.
              </p>

              <p className={`text-base leading-relaxed ${bodyMuted}`}>
                Nothing is carried overnight. Your models and execution logic
                never leave your perimeter.
              </p>

              <div className="flex flex-wrap gap-4 pt-1">
                <Link
                  to="/contact?subject=PATi%20Executive%20Briefing"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-600 text-slate-900 font-bold rounded-xl transition-colors"
                >
                  Request A Live Demo
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact?subject=PATi%20Architecture%20Review"
                  className={`inline-flex items-center gap-2 px-8 py-4 font-semibold rounded-xl border transition-colors ${
                    isDark
                      ? 'border-slate-600 text-slate-200 hover:border-teal-500 hover:text-teal-300'
                      : 'border-slate-300 text-slate-700 hover:border-teal-600 hover:text-teal-800'
                  }`}
                >
                  Architecture Review
                </Link>
              </div>
            </div>

            <div className="w-full min-w-0">
              <PATiFollowTheClockDiagram theme={theme} />
            </div>
          </div>
        </div>
      </section>

      {/* Session handoff */}
      <section
        className={`py-14 lg:py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>Follow The Clock</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Each Market Runs Its Own Day, Then Goes Flat
          </h3>
          <p className={`max-w-2xl mb-10 ${bodyMuted}`}>
            New York builds its list, works it, and closes out. London does the
            same, then Tokyo. Each venue keeps its own targets and controls, so
            a position opened in one session never becomes another market&apos;s
            problem the next morning.
          </p>
          <PATiSessionOrchestrationDiagram theme={theme} />
        </div>
      </section>

      {/* Fail-closed */}
      <section
        className={`py-14 lg:py-20 ${isDark ? 'bg-slate-950/50' : 'bg-slate-50'}`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>Pre-Trade Discipline</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">
            Ambiguous State Does Not Trade
          </h3>
          <p className={`max-w-2xl mb-10 ${bodyMuted}`}>
            Broker health, session validity, data integrity, and risk budget are
            all checked before an order is released. Any one of them unclear,
            and the order waits. That is the default, not a toggle someone has
            to remember.
          </p>
          <PATiReadinessGateDiagram theme={theme} />
        </div>
      </section>

      {/* Ownership */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>What Stays Yours</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-8">
            Your Signals Do Not Leave The Building
          </h3>
          <div className="grid sm:grid-cols-3 gap-5">
            {ownership.map(({ icon: Icon, title, detail }) => (
              <div key={title} className={`p-6 rounded-xl ${cardShell}`}>
                <Icon className="w-6 h-6 mb-3 text-teal-400" aria-hidden />
                <h4 className="font-bold mb-2">{title}</h4>
                <p className={`text-sm leading-relaxed ${bodyMuted}`}>
                  {detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two audiences */}
      <section
        className={`py-14 lg:py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
      >
        <div className="max-w-6xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>Who Has To Be Satisfied</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-10">
            Two People, Two Different Tests
          </h3>
          <div className="grid lg:grid-cols-2 gap-6">
            <div
              className={`p-7 rounded-2xl border-l-4 border-l-indigo-500 ${cardShell}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <Building2 className="w-5 h-5 text-indigo-400" aria-hidden />
                <h4 className="font-bold text-lg">If You Own The Capital</h4>
              </div>
              <ul className="space-y-3">
                {principalConcerns.map(item => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2
                      className={`w-4 h-4 flex-shrink-0 mt-1 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}
                      aria-hidden
                    />
                    <span className={`text-sm leading-relaxed ${bodyStrong}`}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={`p-7 rounded-2xl border-l-4 border-l-teal-500 ${cardShell}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <LineChart className="w-5 h-5 text-teal-400" aria-hidden />
                <h4 className="font-bold text-lg">If You Run The Desk</h4>
              </div>
              <ul className="space-y-3">
                {deskConcerns.map(item => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2
                      className={`w-4 h-4 flex-shrink-0 mt-1 ${isDark ? 'text-teal-400' : 'text-teal-600'}`}
                      aria-hidden
                    />
                    <span className={`text-sm leading-relaxed ${bodyStrong}`}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Demo */}
      <section
        className={`py-14 lg:py-20 ${isDark ? 'bg-slate-950/80' : 'bg-slate-50'}`}
      >
        <div className="max-w-4xl mx-auto px-6">
          <p className={`${eyebrow} mb-3`}>The Demo</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-8">
            What We Put On The Screen
          </h3>
          <div className="grid sm:grid-cols-2 gap-5">
            {demoItems.map(item => (
              <div key={item} className={`p-5 rounded-xl ${cardShell}`}>
                <p className={`text-sm leading-relaxed ${bodyStrong}`}>
                  {item}
                </p>
              </div>
            ))}
          </div>
          <p className={`mt-8 text-lg font-semibold ${bodyStrong}`}>
            Bring your own scenario. Bring your risk officer.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section
        className={`py-16 lg:py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}
      >
        <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
          <h3
            className={`text-3xl lg:text-4xl font-bold leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            If You Run Money Across Sessions, This Is A Short Conversation
          </h3>
          <Link
            to="/contact?subject=PATi%20Conversation"
            className="inline-flex items-center gap-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors"
          >
            Request A Live Demo
            <ArrowRight className="w-5 h-5" aria-hidden />
          </Link>
          <p
            className={`text-xs leading-relaxed max-w-2xl mx-auto pt-4 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
          >
            PATi is trading infrastructure software, not investment advice, and
            not an offer to buy or sell any security. Trading involves risk of
            loss, including loss of principal. Shared Oxygen makes no
            representation regarding trading results.
          </p>
          <p
            className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            PATi is a product of Shared Oxygen, LLC
          </p>
        </div>
      </section>
    </div>
  );
};

export default PATi;
